(() => {
  'use strict';

  const BUY_NOW_SELECTOR = [
    '.fb-product-main__buy-now',
    '.fb-qv-buy-now',
    '[data-fb-buy-now]',
  ].join(',');

  const SHIPPING_SELECTION_KEY = 'fb_shipping_selection_v3';
  const CHECKOUT_WAIT_MS = 14000;

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const cartUrl = () => {
    const configured = window.Theme?.routes?.cart_url || window.Shopify?.routes?.cart_url;
    return configured || `${rootUrl()}cart`;
  };

  const checkoutUrl = () => `${rootUrl()}checkout`;
  const addUrl = () => `${rootUrl()}cart/add.js`;

  const checkoutButtonFrom = (element) => {
    if (!(element instanceof Element)) return null;
    return element.closest('#checkout');
  };

  const isDrawerCheckout = (element) => {
    const checkout = checkoutButtonFrom(element);
    if (!checkout) return false;

    return Boolean(
      checkout.closest(
        'theme-drawer#cart-drawer, cart-drawer-component, #shopify-section-cart-drawer-section'
      )
    );
  };

  const hasShippingSelection = () => {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(SHIPPING_SELECTION_KEY) || 'null');
      return Boolean(value?.key && value?.name);
    } catch (_) {
      return false;
    }
  };

  const findProductRoot = (button) => {
    return (
      button.closest('.fb-product-main') ||
      button.closest('#quick-add-modal-content') ||
      button.closest('quick-add-modal') ||
      button.closest('product-form-component') ||
      button.closest('section') ||
      document
    );
  };

  const isCartAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute('action') || '';
    return action.includes('/cart/add');
  };

  const findProductForm = (button) => {
    if (isCartAddForm(button.form)) return button.form;

    const root = findProductRoot(button);
    const forms = Array.from(root.querySelectorAll('form')).filter(isCartAddForm);

    if (forms.length === 1) return forms[0];

    return forms.find((form) => form.querySelector('[name="id"]')) || null;
  };

  const buildFallbackFormData = (button) => {
    const root = findProductRoot(button);
    const variantInput = root.querySelector(
      'input[name="id"]:not([disabled]), select[name="id"]:not([disabled])'
    );

    const variantId =
      variantInput?.value ||
      button.dataset.variantId ||
      root.dataset.variantId ||
      '';

    if (!variantId) return null;

    const quantityInput = root.querySelector(
      'input[name="quantity"]:not([disabled]), select[name="quantity"]:not([disabled])'
    );

    const quantity = Math.max(1, Number(quantityInput?.value || button.dataset.quantity || 1));

    const data = new FormData();
    data.append('id', variantId);
    data.append('quantity', String(quantity));
    return data;
  };

  const getAddData = (button) => {
    const form = findProductForm(button);

    if (form) {
      const data = new FormData(form);
      if (data.get('id')) return data;
    }

    return buildFallbackFormData(button);
  };

  const setBusy = (button, busy) => {
    if (!(button instanceof HTMLButtonElement)) return;

    if (busy) {
      button.dataset.fbWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.disabled = button.dataset.fbWasDisabled === 'true';
      button.removeAttribute('aria-busy');
      delete button.dataset.fbWasDisabled;
    }
  };

  const showAddError = (message) => {
    console.error('[Forever Bloom] Comprar ahora:', message);
    window.alert(
      message ||
        'No pudimos añadir este producto al carrito. Revisa la selección e inténtalo de nuevo.'
    );
  };

  const addAndGoToCart = async (button, data) => {
    setBusy(button, true);

    try {
      const response = await fetch(addUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: data,
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch (_) {
          // Ignore non-JSON response.
        }

        throw new Error(
          payload?.description ||
            payload?.message ||
            'No pudimos añadir este producto al carrito.'
        );
      }

      window.location.assign(cartUrl());
    } catch (error) {
      setBusy(button, false);
      showAddError(error?.message);
    }
  };

  const withTimeout = (promise, ms) =>
    Promise.race([
      promise,
      new Promise((resolve) => window.setTimeout(() => resolve(null), ms)),
    ]);

  const prepareShippingCheckout = async () => {
    if (!hasShippingSelection()) return '';

    try {
      if (typeof window.__fbShippingPrepareCheckout === 'function') {
        const result = await withTimeout(
          Promise.resolve(window.__fbShippingPrepareCheckout()),
          CHECKOUT_WAIT_MS
        );
        return result?.checkoutUrl || window.__fbShippingCheckoutUrl || '';
      }

      if (window.__fbShippingPersistencePromise) {
        const result = await withTimeout(
          Promise.resolve(window.__fbShippingPersistencePromise),
          CHECKOUT_WAIT_MS
        );
        return result?.checkoutUrl || window.__fbShippingCheckoutUrl || '';
      }
    } catch (error) {
      console.warn(
        '[Forever Bloom] No se pudo preparar la tarifa elegida antes del checkout:',
        error?.message || error
      );
    }

    return window.__fbShippingCheckoutUrl || '';
  };

  const goDirectToCheckout = async (button) => {
    setBusy(button, true);

    const preparedUrl = await prepareShippingCheckout();
    window.location.assign(preparedUrl || checkoutUrl());
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const checkout = checkoutButtonFrom(target);
      if (checkout) {
        const drawerCheckout = isDrawerCheckout(checkout);
        const preserveShipping = hasShippingSelection();

        // Drawer: Pagar siempre va directo al checkout.
        // Página /cart: solo interceptamos cuando existe una tarifa elegida que hay que conservar.
        if (drawerCheckout || preserveShipping) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          goDirectToCheckout(checkout);
          return;
        }
      }

      // Comprar ahora sigue añadiendo el producto y llevando a /cart.
      const buyNow = target.closest(BUY_NOW_SELECTOR);
      if (!(buyNow instanceof HTMLElement)) return;
      if (buyNow.matches(':disabled, [aria-disabled="true"]')) return;

      const data = getAddData(buyNow);

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!data) {
        showAddError(
          'No pudimos identificar la variante seleccionada. No se ha enviado al checkout para evitar una compra incorrecta.'
        );
        return;
      }

      addAndGoToCart(buyNow, data);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      if (!(submitter instanceof Element)) return;

      const checkout = checkoutButtonFrom(submitter);
      if (!checkout) return;

      const drawerCheckout = isDrawerCheckout(checkout);
      const preserveShipping = hasShippingSelection();
      if (!drawerCheckout && !preserveShipping) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      goDirectToCheckout(checkout);
    },
    true
  );
})();
